import { EditBomSupplierInfoModal } from "@/components/bom/edit-bom-supplier-info-modal";
import { Button } from "@/components/core/buttons/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/core/card";
import { Content } from "@/components/layouts/content";
import { bomSupplierInfoQueryOptions } from "@/features/dashboard/api/queries/bom-supplier-info-query";
import { BUSINESS_TYPE_LABELS, TAX_REGISTRATION_OPTIONS } from "@/features/dashboard/types/bom-supplier-info";
import { IconArrowLeft, IconCheck, IconEdit, IconExternalLink, IconFileText } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useState } from "react";

export const Route = createFileRoute("/_dashboard/bom_/supplier-infos_/$supplierInfoId")({
	component: SupplierInfoDetailPage,
});

function Field({ label, value }: { label: string; value: React.ReactNode }) {
	return (
		<div className="flex flex-col gap-0.5">
			<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">{label}</span>
			<span className="text-sm text-gray-900">{value ?? <span className="text-gray-400">—</span>}</span>
		</div>
	);
}

function SupplierInfoDetailPage() {
	const { supplierInfoId } = Route.useParams();
	const [isEditOpen, setIsEditOpen] = useState(false);

	const supplierQuery = useQuery(bomSupplierInfoQueryOptions(supplierInfoId));
	const supplier = supplierQuery.data;

	if (supplierQuery.isLoading) {
		return (
			<Content pageName="Supplier Details">
				<div className="flex items-center justify-center h-64">
					<div className="text-center">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
						<p className="mt-2 text-sm text-gray-600">Loading supplier details...</p>
					</div>
				</div>
			</Content>
		);
	}

	if (!supplier) {
		return (
			<Content pageName="Supplier Details">
				<div className="flex flex-col items-center justify-center h-64 gap-2">
					<span className="text-gray-600">Supplier not found.</span>
					<Link to="/bom/supplier-infos" className="text-blue-600 hover:underline">
						Back to suppliers
					</Link>
				</div>
			</Content>
		);
	}

	const businessTypeLabel = supplier.business_type ? BUSINESS_TYPE_LABELS[supplier.business_type] : null;
	const taxRegistrationLabel = supplier.tax_registration ? TAX_REGISTRATION_OPTIONS.find((opt) => opt.value === supplier.tax_registration)?.label : null;

	return (
		<Content pageName="Supplier Details">
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<div className="flex flex-row items-center justify-between w-full gap-4">
					<div className="flex flex-col gap-1">
						<Link to="/bom/supplier-infos" className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 w-fit">
							<IconArrowLeft className="h-4 w-4 mr-1" />
							Back to Suppliers
						</Link>
						<span className="text-xl text-foreground-dark font-semibold">{supplier.supplier_name ?? "Untitled Supplier"}</span>
						{supplier.address && <span className="text-base text-foreground/70">{supplier.address}</span>}
					</div>
					<Button onClick={() => setIsEditOpen(true)}>
						<IconEdit size={18} stroke={2} />
						<span>Edit</span>
					</Button>
				</div>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">General Information</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<Field label="TIN" value={supplier.tin} />
						<Field label="Business Type" value={businessTypeLabel} />
						<Field label="Year Established" value={supplier.year_established} />
						<Field label="Years in Business" value={supplier.years_in_business} />
						<Field label="Product / Service Category" value={supplier.product_service_category} />
						<Field label="Terms" value={supplier.terms} />
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Tax Information</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<Field label="Tax Registration" value={taxRegistrationLabel} />
						{supplier.tax_registration === "vat" && <Field label="VAT Rate" value={supplier.vat_rate != null ? `${supplier.vat_rate}%` : null} />}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Contact Person</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<Field label="Contact Person" value={supplier.contact_person} />
						<Field label="Position" value={supplier.position} />
						<Field label="Email" value={supplier.email} />
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Contact Methods</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 md:grid-cols-3 gap-4">
						<Field label="Landline" value={supplier.landline_number} />
						<Field label="Mobile" value={supplier.mobile_number} />
						<Field label="Viber" value={supplier.viber} />
						<Field label="Viber Group" value={supplier.viber_group} />
						<Field label="Skype" value={supplier.skype} />
						<Field
							label="Website"
							value={
								supplier.website ? (
									<a href={supplier.website} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
										{supplier.website}
									</a>
								) : null
							}
						/>
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Company Proprietor</CardTitle>
					</CardHeader>
					<CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
						<Field label="Proprietor" value={supplier.company_proprietor} />
						<Field label="Proprietor Position" value={supplier.company_proprietor_position} />
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Required Documents</CardTitle>
					</CardHeader>
					<CardContent>
						{!supplier.required_documents || supplier.required_documents.length === 0 ? (
							<span className="text-sm text-gray-400 italic">No documents on file.</span>
						) : (
							<div className="flex flex-col divide-y divide-gray-200">
								{supplier.required_documents.map((doc, index) => (
									<div key={doc.id ?? doc.document_name ?? index} className="grid grid-cols-1 md:grid-cols-12 gap-3 py-3 items-start">
										<div className="md:col-span-5 flex items-start gap-2">
											<IconFileText size={18} className="mt-0.5 text-gray-500 shrink-0" />
											<div className="flex flex-col">
												<span className="text-sm font-medium text-gray-900">{doc.document_name}</span>
												{doc.verified ? (
													<span className="inline-flex items-center gap-1 text-xs text-green-700 mt-0.5">
														<IconCheck size={12} stroke={3} /> Verified
													</span>
												) : (
													<span className="text-xs text-gray-400 mt-0.5">Not verified</span>
												)}
											</div>
										</div>
										<div className="md:col-span-3 text-sm">
											{doc.pdf_url ? (
												<a href={doc.pdf_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-blue-600 hover:underline">
													<IconExternalLink size={14} /> View PDF
												</a>
											) : (
												<span className="text-gray-400">No file uploaded</span>
											)}
										</div>
										<div className="md:col-span-4 text-sm text-gray-700">
											{doc.risk_if_none ? (
												<div className="flex flex-col">
													<span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Risk if None</span>
													<span>{doc.risk_if_none}</span>
												</div>
											) : (
												<span className="text-gray-400">—</span>
											)}
										</div>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>

				<Card>
					<CardHeader>
						<CardTitle className="text-lg">Bank Details</CardTitle>
					</CardHeader>
					<CardContent>
						{!supplier.bank_details || supplier.bank_details.length === 0 ? (
							<span className="text-sm text-gray-400 italic">No bank accounts on file.</span>
						) : (
							<div className="flex flex-col gap-4">
								{supplier.bank_details.map((detail, index) => (
									<div key={detail.id ?? index} className="border border-gray-200 rounded-md p-4">
										<div className="font-medium text-gray-600 mb-3">Bank Account #{index + 1}</div>
										<div className="grid grid-cols-1 md:grid-cols-3 gap-4">
											<Field label="Bank" value={detail.bank} />
											<Field label="Account Name" value={detail.account_name} />
											<Field label="Account Number" value={detail.account_number} />
											<Field label="Type of Account" value={detail.type_of_account} />
											<Field label="Bank Branch" value={detail.bank_branch} />
										</div>
									</div>
								))}
							</div>
						)}
					</CardContent>
				</Card>
			</div>

			<EditBomSupplierInfoModal supplierInfo={supplier} isOpen={isEditOpen} onClose={() => setIsEditOpen(false)} onSuccess={() => supplierQuery.refetch()} />
		</Content>
	);
}
